import { Wrapper } from '@googlemaps/react-wrapper';
import isElectron from 'is-electron';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Map } from '../Map/Map';
import {
  SSearchWithButton,
  SLocationButton,
  SSearchBarBase,
  TopControlContainer,
  DragInfoBanner,
  SCenterMarkerWrapper,
} from './SearchableMap.styles';
import { ReactComponent as Geo } from '../../assets/icons/crosshair--yellow.svg';
import { useConfirmation } from '../../utils/ConfirmationServiceContext/confirmationContext';
import Geocoder, { apiKey } from '../../utils/geocoder';
import { debounce } from 'lodash';
import { shouldUseMapAccuracy } from '../../utils/features';
import { Circle } from '../Map/MapElements/MapCircle';
import { useTranslation } from 'react-i18next';
import { LocationMarker } from '../Map/LocationMarker';

const zoomDriftThresholdM = 3;

const degToRad = Math.PI / 180;
const mPerLat = 111_132;

type Location = google.maps.LatLngLiteral & {
  accuracy?: number;
};

type SearchableMapProps = {
  style?: React.CSSProperties;
  buttonText?: string;
  isLoading?: boolean;
  buttonStyle?: React.CSSProperties;
  buttonTextStyle?: React.CSSProperties;
  lng?: number;
  lat?: number;
  location?: Location;
  setLocation: React.Dispatch<React.SetStateAction<Location | undefined>>;
  setLocName?: React.Dispatch<React.SetStateAction<string>>;
};

const defaultZoom = 18;

function SearchableMap(props: SearchableMapProps) {
  const { location, setLocation, setLocName } = props;

  const confirm = useConfirmation();

  const [mounted, setMounted] = useState(false);

  const [locationName, setLocationName] = useState('');

  // Backward compatibility states.
  // Can be removed after verifying and migrating to `shouldUseMapAccuracy`
  // https://github.com/cosafe/web/issues/1955
  const [camZoom, setZoom] = useState(shouldUseMapAccuracy ? undefined : defaultZoom);
  const [camCenter, setCenter] = useState<google.maps.LatLngLiteral | undefined>(undefined);
  const lastKnownGpsRef = useRef<google.maps.LatLngLiteral | undefined>(undefined);

  const mapRef = useRef<google.maps.Map | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);
  const accuracyCircleRef = useRef<google.maps.Circle>(null);

  async function updateLocationName(latLngLiteral: google.maps.LatLngLiteral) {
    if (!latLngLiteral?.lat) {
      return;
    }

    try {
      const res = await Geocoder({
        latitude: latLngLiteral.lat,
        longitude: latLngLiteral.lng,
      });
      if (!res) {
        return;
      }

      const locName = res.addressName ? res.addressName : '';
      setLocationName(locName);
      setLocName?.(locName);
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    if (location) {
      updateLocationName(location);
    }
  }, [location]);

  useEffect(() => {
    if (inputRef.current) inputRef.current.value = locationName;
  }, [locationName]);

  const showTurnOnLocationDialog = () => {
    if (isElectron()) {
      confirm({
        description: 'turn_on_location_intro',
        onSubmit: () => window.electronApi?.sendLocationServicesEnable(),
        onCancel: () => {},
        confirmText: 'ok',
      });
    }
  };

  const onCreated = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          const { latitude: lat, longitude: lng, accuracy } = position.coords;

          setLocation({ lat, lng, accuracy });
          adjustCamera(position.coords);
          lastKnownGpsRef.current = { lat, lng };
        },
        e => {
          showTurnOnLocationDialog();
          console.error("Can't get location", e);
        },
        { maximumAge: 5000, enableHighAccuracy: true, timeout: 15000 }
      );
    } else {
      // Browser doesn't support Geolocation
      console.warn("Browser doesn't support Geolocation");
    }
    setMounted(true);
  };

  useEffect(() => {
    onCreated();
    return () => {
      setMounted(false);
    };
  }, []);

  // Backward compatibility callback.
  // Can be removed after verifying and migrating to `shouldUseMapAccuracy`
  // https://github.com/cosafe/web/issues/1955
  const onIdle = useIgnoreFirstInvocation((m: google.maps.Map) => {
    if (shouldUseMapAccuracy) return;

    if (m) {
      const mapZoom = m.getZoom();
      if (mapZoom) {
        setZoom(mapZoom);
      }
      const mapValue = m.getCenter();
      if (mapValue) {
        setCenter(mapValue.toJSON());
      }
    }
  });

  const adjustCamera = (coords: GeolocationCoordinates) => {
    const { latitude: lat, longitude: lng, accuracy } = coords;

    if (!shouldUseMapAccuracy) {
      setCenter({ lat, lng });
      setZoom(defaultZoom);
      return;
    }

    const map = mapRef.current;
    const circle = accuracyCircleRef.current;
    if (!map || !circle) return;

    circle.setCenter({ lat, lng });
    circle.setRadius(accuracy);

    setTimeout(() => {
      const nextBounds = circle.getBounds();
      if (nextBounds) circle.getMap()?.fitBounds(nextBounds, 20);
    }, 100);
  };

  const setUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async position => {
          if (mounted) {
            const { latitude: lat, longitude: lng, accuracy } = position.coords;

            setLocation({ lat, lng, accuracy });
            adjustCamera(position.coords);
            lastKnownGpsRef.current = { lat, lng };
          }
        },
        async error => {
          console.error('Geolocation Error' + error.code, error.message);
          showTurnOnLocationDialog();
        },
        { maximumAge: 5000, enableHighAccuracy: true, timeout: 15000 }
      );
    }
  };

  const onBoundsChange = useIgnoreFirstInvocation((map: google.maps.Map) => {
    const latLng = map.getCenter()?.toJSON();
    if (!latLng) throw new Error('Map center on bounds change not provided.');

    const shouldResetAccuracy =
      !lastKnownGpsRef.current ||
      latLonDiffToM(latLng, lastKnownGpsRef.current) > zoomDriftThresholdM;

    if (mounted) {
      setLocation(currentLocation => ({
        ...currentLocation,
        accuracy: shouldResetAccuracy ? undefined : currentLocation?.accuracy,
        lat: latLng.lat,
        lng: latLng.lng,
      }));
    }
  });

  const debouncedOnBoundsChange = debounce(onBoundsChange, 500, {
    leading: false,
    trailing: true,
  });

  const debounceSetLocation = useCallback(
    debounce(setLocation, 500, {
      leading: false,
      trailing: true,
    }),
    []
  );

  return (
    <>
      <TopControlContainer className="searchWithButton">
        <SSearchWithButton role="searchWithButton">
          <SSearchBarBase
            fallback={() => {}}
            placeholderTx="documents_search"
            forwardedRef={inputRef}
          />
          <SLocationButton onClick={setUserLocation} role="locationButton">
            <Geo role="locationIcon" />
          </SLocationButton>
        </SSearchWithButton>
        {!location && <DragInfoBannerWithDelay delay={1000} />}
      </TopControlContainer>
      {/* The marker was eyeballed into center, when making changes compare to original marker */}
      <SCenterMarkerWrapper role="markerContainer">
        <LocationMarker movable withPin={!!location} />
      </SCenterMarkerWrapper>
      <Wrapper apiKey={apiKey!} libraries={['places']}>
        <Map
          ref={mapRef}
          inputRef={inputRef}
          center={camCenter}
          onIdle={onIdle}
          zoom={camZoom}
          className="fullHeightMap"
          disableDefaultUI
          onSearch={(lat, lng) => debounceSetLocation({ lat: lat, lng: lng })}
          clickableIcons={false}
          onBoundsChange={debouncedOnBoundsChange}
          onDragStart={() =>
            setLocation(
              currentLocation => currentLocation && { ...currentLocation, accuracy: undefined }
            )
          }
        >
          {shouldUseMapAccuracy && location?.accuracy && (
            <Circle
              ref={accuracyCircleRef}
              center={{ lat: location.lat, lng: location.lng }}
              radius={location.accuracy}
            />
          )}
        </Map>
      </Wrapper>
    </>
  );
}

export default SearchableMap;

/*
 * Google Maps triggers some events on the first render, which we don't want to handle.
 * This hook ignores the first invocation of the provided function.
 */
const useIgnoreFirstInvocation = <P extends unknown[], R>(fn: (...params: P) => R) => {
  const firstInvocationDone = useRef(false);

  return (...params: P) => {
    if (!firstInvocationDone.current) {
      firstInvocationDone.current = true;
      return;
    }

    return fn(...params);
  };
};

/**
 * Shows the banner with a delay in case the location is available right away, in order not
 * to flash it = not to appear glitchy.
 */
const DragInfoBannerWithDelay = ({ delay }: { delay: number }) => {
  const { t } = useTranslation();

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setVisible(true), delay);

    return () => clearTimeout(timeout);
  }, []);

  if (!visible) return null;

  return (
    <DragInfoBanner>
      <span>{t('drag_pin_or_search')}</span>
    </DragInfoBanner>
  );
};

const latLonDiffToM = (l: google.maps.LatLngLiteral, r: google.maps.LatLngLiteral) => {
  const avLat = (l.lat + r.lat) / 2;
  const latDiffM = (r.lat - l.lat) * mPerLat;
  const lonDiffM = (r.lng - l.lng) * mPerLat * Math.cos(avLat * degToRad);
  return Math.sqrt(latDiffM ** 2 + lonDiffM ** 2);
};
